import axios from "axios";
import { toast } from "react-toastify";

// ============================================================================
// Axios Instance Configuration
// ============================================================================
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}`,
  withCredentials: true,
});

// ============================================================================
// Global State for Error Reporting
// ============================================================================
let lastReportTime = 0;
const REPORT_INTERVAL = 5 * 60 * 1000; // 5 minutes

// ============================================================================
// Request Interceptor
// ============================================================================
axiosInstance.interceptors.request.use(
  (config) => {
    // Add static Product ID only. Do NOT send X-TenantID — backend
    // currently issues/validates all tokens as PUBLIC.
    config.headers["X-ProductID"] = process.env.REACT_APP_PRODUCT_ID;

    const jwtToken = sessionStorage.getItem("jwtToken");

    if (jwtToken) {
      config.headers.Authorization = jwtToken;
      config.headers["X-ProductID"] = process.env.REACT_APP_PRODUCT_ID;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================================
// Response Interceptor
// ============================================================================
axiosInstance.interceptors.response.use(
  (response) => {
    const newJwtToken =
      response.headers["authorization"] || response.headers["Authorization"];
    if (newJwtToken) {
      sessionStorage.setItem("jwtToken", newJwtToken);
    }
    return response;
  },

  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const isSessionCheck = originalRequest.url.endsWith("/api/session");
      const isRefreshTokenCall = originalRequest.url.endsWith("/refresh-token");
      const isLoginCall = originalRequest.url.endsWith("/employeeLogin");

      if (isSessionCheck || isRefreshTokenCall || isLoginCall) {
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await axiosInstance.post("/refresh-token");

        const newJwtToken =
          refreshResponse.headers["authorization"] ||
          refreshResponse.headers["Authorization"];
        if (newJwtToken) {
          sessionStorage.setItem("jwtToken", newJwtToken);
          const cleanToken = newJwtToken.replace(/^Bearer\s+/i, "");
          originalRequest.headers.Authorization = `Bearer ${cleanToken}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        sessionStorage.removeItem("jwtToken");
        console.error("Session refresh failed. Logging out.", refreshError);

        if (
          window.location.pathname !== "/" &&
          window.location.pathname !== "/login"
        ) {
          window.location.href = "/";
        }

        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 409) {
      toast.error(
        error.response.data?.message ||
          "Conflict occurred. Please check your input.",
      );
      return Promise.reject(error);
    }

    toast.error("Some error occurred. Please try again later.");

    if (error.response?.status === 500) {
      const currentTime = Date.now();
      if (currentTime - lastReportTime >= REPORT_INTERVAL) {
        lastReportTime = currentTime;
        const reportDetails = {
          title: "Server Error: 500",
          description: JSON.stringify({
            message: error.response.data?.message || "Unknown server error",
            status: error.response.status,
            url: error.config.url,
            method: error.config.method,
          }),
        };

        try {
          await axiosInstance.post(`/bugs-report`, reportDetails);
          console.log("500 error report sent successfully.");
        } catch (reportError) {
          console.error("Failed to send 500 error report:", reportError);
        }
      }
    }

    if (
      error.response?.status === 501 &&
      window.location.pathname !== "/error-500"
    ) {
      window.location.href = "/error-500";
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
